// What the accounting package has actually done, and nothing more.
//
// Shared by every screen that can start an external write, and shared on
// purpose: this is the one place in the app that may say an effect exists, and
// a rule enforced once is a rule. A second screen re-implementing the poll
// would be a second chance to say "matched" before a receipt says so, and the
// double-payment guarantee the worker and the approval endpoint keep would be
// undone by the cheapest possible bug — a copied component that drifted.
//
// contracts/http-api.md: "Only the operation read endpoint reports confirmed
// external effect." So a caller hands this component an operation id from a
// 202 and nothing else; whether anything happened is a question it asks
// GET /operations/{id}, repeatedly, and reports verbatim.
//
// ch.06: "Show an unknown outcome as requiring a status check, not as a failed
// booking eligible for blind retry." There is therefore no retry control here
// at all. Retrying is `POST /operations/{id}/retry`, which the server permits
// only from `failed_safe`, and it belongs on an operations screen where the
// eligibility can be explained — not one click from a state that means we do
// not know.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { request, tenantPath } from '../api/client.ts';
import type { Operation } from '../api/types.ts';
import { useT } from '../i18n/index.tsx';
import { OPERATION_STATES, useStates } from '../states.ts';
import { StateBadge } from './StateBadge.tsx';
import { StepTracker, type Step } from './StepTracker.tsx';

const OPERATION_STEPS = [
  { key: 'state.external.step.approved', icon: 'user' },
  { key: 'state.external.step.queued', icon: 'clock' },
  { key: 'state.external.step.sending', icon: 'send' },
  { key: 'state.external.step.confirmed', icon: 'check' },
] as const;

/**
 * How far along the happy path the operation's state PROVES it is. Only
 * `confirmed` fills the last step. The off-path states return null and get no
 * tracker, because filling three of four steps beside "Outcome unknown" draws
 * an outcome nobody has established.
 */
function operationProgress(state: string): { reached: number; moving: boolean } | null {
  if (state === 'queued') return { reached: 2, moving: false };
  if (state === 'submitting') return { reached: 2, moving: true };
  if (state === 'confirmed') return { reached: 4, moving: false };
  return null;
}

/** How often the screen asks the API what happened to an operation. */
export const POLL_INTERVAL_MS = 2000;
/** Stop asking after this many tries and let the reviewer decide what to do. */
export const POLL_MAX_ATTEMPTS = 15;

/** States an operation will not leave on its own. */
export function isSettled(state: string): boolean {
  return state === 'confirmed' || state === 'failed_safe' || state === 'needs_review';
}

/** The subset of an operation a screen already has before it polls. */
export interface KnownOperation {
  readonly id: string;
  readonly operationName: string;
  readonly state: string;
}

export interface ExternalStateProps {
  /** Operations a read endpoint already reported, if any. */
  readonly operations: readonly KnownOperation[];
  /** An operation id from a 202, to watch until it settles. */
  readonly watchedOperationId: string | null;
  readonly tenantId: string;
  readonly token: string;
  /** Called once, when the watched operation reaches a state it will not leave. */
  readonly onSettled: () => void;
  /** What to say when the watcher gives up; the screen knows how to get back here. */
  readonly stillRunningHint: string;
}

export function ExternalState({
  operations,
  watchedOperationId,
  tenantId,
  token,
  onSettled,
  stillRunningHint,
}: ExternalStateProps): ReactNode {
  const t = useT();
  const states = useStates();
  const steps: readonly Step[] = OPERATION_STEPS.map((step) => ({ label: t(step.key), icon: step.icon }));
  // Every operation on screen that has not settled is asked about, not only
  // the one this reviewer just approved: an operation already in flight when
  // the page opened moves just the same, and a tracker that showed its state at
  // page load forever would be a picture of the past labelled as the present.
  const [polled, setPolled] = useState<ReadonlyMap<string, Operation>>(new Map());
  const [gaveUp, setGaveUp] = useState(false);

  // The callback lives in a ref rather than in the dependency list. A parent
  // that passes an inline arrow gives this effect a new identity on every
  // render, and an effect that restarts on every render restarts the polling
  // with it — so the operation would be asked about forever and the attempt
  // limit would never be reached.
  const onSettledRef = useRef(onSettled);
  useEffect(() => {
    onSettledRef.current = onSettled;
  }, [onSettled]);

  const unsettledKnown = operations.filter((operation) => !isSettled(operation.state)).map((operation) => operation.id);
  const watchKey = [...new Set([...(watchedOperationId === null ? [] : [watchedOperationId]), ...unsettledKnown])]
    .sort()
    .join(',');

  useEffect(() => {
    if (watchKey === '') return;
    const ids = watchKey.split(',');
    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const open = new Set(ids);
    setGaveUp(false);

    const poll = async (): Promise<void> => {
      if (cancelled) return;
      attempts += 1;
      // One at a time, in a fixed order: there are rarely more than two.
      for (const id of [...open]) {
        try {
          const operation = await request<Operation>(
            tenantPath(tenantId, `/operations/${encodeURIComponent(id)}`),
            { token },
          );
          if (cancelled) return;
          setPolled((current) => new Map(current).set(id, operation));
          if (isSettled(operation.state)) {
            open.delete(id);
            onSettledRef.current();
          }
        } catch {
          // A failed poll says nothing about the operation. Keep the last state
          // shown and try again; never downgrade it to an error.
        }
      }
      if (cancelled || open.size === 0) return;
      if (attempts >= POLL_MAX_ATTEMPTS) {
        setGaveUp(true);
        return;
      }
      timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
    };

    // Once immediately — the reviewer has just approved, or just opened the
    // document — and then on the interval, each attempt scheduling the next so
    // there is only ever one chain in flight.
    void poll();
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [watchKey, tenantId, token]);

  const known = new Map<string, KnownOperation>(operations.map((operation) => [operation.id, operation]));
  for (const [id, operation] of polled) known.set(id, operation);
  // The watched one first, as before; then the rest in the order the read gave.
  const order = [
    ...(watchedOperationId !== null && known.has(watchedOperationId) ? [watchedOperationId] : []),
    ...operations.map((operation) => operation.id).filter((id) => id !== watchedOperationId),
  ];
  const rows: readonly KnownOperation[] = order.map((id) => known.get(id) as KnownOperation);

  if (rows.length === 0) return null;

  return (
    <div className="card" data-testid="external-state">
      <div className="card-head">
        <div>
          <h2>{t('state.external.title')}</h2>
          <p>{t('state.external.intro')}</p>
        </div>
      </div>
      {rows.map((operation) => {
        const shown = states.display(OPERATION_STATES, operation.state);
        const receipts = 'receipts' in operation ? (operation as Operation).receipts : [];
        const progress = operationProgress(operation.state);
        return (
          <div className="row" key={operation.id}>
            <div className="grow">
              <strong>{states.operationName(operation.operationName)}</strong>
              <p>{shown.explanation}</p>
              {progress === null ? null : <StepTracker steps={steps} {...progress} />}
              {receipts.length > 0 ? (
                <p>
                  {receipts.map((receipt) => (
                    <span key={receipt.id}>
                      {states.receipt(receipt.state)} · {receipt.externalRef}
                    </span>
                  ))}
                </p>
              ) : null}
              {shown.nextStep === 'check_status' ? (
                <p>{t('state.external.checkStatusOnly')}</p>
              ) : null}
            </div>
            <StateBadge table={OPERATION_STATES} state={operation.state} />
          </div>
        );
      })}
      {gaveUp ? <div className="card-foot">{stillRunningHint}</div> : null}
    </div>
  );
}
